import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import { basicAvailableUsdAtom, formatUsd } from '@root/entities/basic'
import { useTranslation } from '@root/shared/localization'

type Props = {
  onDeposit: () => void
}

/**
 * The one number Basic has.
 *
 * Dollars, spendable, and nothing else — no wallet to pick, no SOL, no gas
 * kept back. What is shown is what a buy on the next line can actually
 * spend, which is the on-chain balance less anything already committed.
 */
export const BasicCashHeader: React.FC<Props> = ({ onDeposit }) => {
  const { t } = useTranslation()
  const [available] = useAtom(basicAvailableUsdAtom)

  const amount = pipe(
    available,
    AsyncStateN.getOrElseW(() => undefined),
  )

  return (
    <Root>
      <Amounts>
        {amount === undefined ? (
          <Skeleton width={72} height={22} />
        ) : (
          <Cash>{formatUsd(amount)}</Cash>
        )}
        <Label>{t('basic.cash')}</Label>
      </Amounts>
      <Deposit type="button" onClick={onDeposit}>
        {t('basic.deposit_more')}
      </Deposit>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 12px;

  background: var(--background-3);
  border-radius: 12px;
`

const Amounts = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: baseline;
  gap: 6px;
`

const Cash = styled.span`
  color: var(--text-12);
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
`

const Label = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
`

const Deposit = styled.button`
  background: transparent;
  border: none;
  padding: 0;

  color: var(--primary-9, var(--text-12));
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`
